import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { fetchCurrencySelectOptions } from '#/api/system/currency';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { SelectCore } from '#/modules/commonComponents/form/select';
import AmountBreakdown from '#/modules/infra/dashboard/components/AmountBreakdown';
import BreakdownSummary from '#/modules/infra/dashboard/components/BreakdownSummary';
import StatusByMonthChart from '#/modules/infra/dashboard/components/StatusByMonthChart';
import StatusProgressBars from '#/modules/infra/dashboard/components/StatusProgressBars';
import { formatCount } from '#/modules/infra/dashboard/helper/dashboardChartTheme';
import { DISTRIBUTION_UNKNOWN_LABEL } from '#/modules/infra/dashboard/helper/dashboardModules';
import {
  DASHBOARD_TREND_MONTH_OPTIONS,
  buildDrillDownHref,
  monthRange,
  readPeriod,
} from '#/modules/infra/dashboard/helper/dashboardRange';
import { lookupQueryKey } from '#/utils/queryKeys';

/** 圖表區固定最小高度：讀取骨架與載入後同高，切換 tab 時區域不伸縮 */
const CHART_SECTION_CLASS = `
  flex min-h-[400px] min-w-0 flex-col gap-2 rounded-lg border border-border bg-background p-3.5
`;

/**
 * 單一 module 的圖表：各月進度（月數＝trend_months，可切 3／6／12）＋（有財務可見性時）單幣別金額摘要與幣別 selector；
 * 進貨／出庫另有單據期間內的來源／類別分布。
 * 資料來自 overview 的 `modules[key]`（不另打細項 API）。
 * - `amounts` key 不存在＝無財務可見性 → 隱藏金額摘要與 selector（不當成 0）。
 * - 幣別整頁共用：任一圓餅換幣別，所有金額 module 一起換；月圖／metrics 不受影響。沒有 `amounts` 時月圖滿版。
 */
const ModuleDashboardPanel = ({
  config,
  moduleData,
  currencyId,
  onCurrencyChange,
  trendMonths,
  onTrendMonthsChange,
  isFetching,
}) => {
  const navigate = useNavigate();
  const hasAmounts = Boolean(moduleData?.amounts);
  const currencyQuery = useQuery({
    queryKey: lookupQueryKey('currencySelect'),
    queryFn: fetchCurrencySelectOptions,
    enabled: hasAmounts,
  });

  const period = readPeriod(config.key, moduleData?.period);
  const distribution = config.distribution ? moduleData?.[config.distribution.key] : null;
  const distributionItems = useMemo(
    () => (distribution?.labels ?? []).map((key, index) => ({
      key,
      label: key === 'UNKNOWN' ? DISTRIBUTION_UNKNOWN_LABEL : (config.distribution.labels[key] ?? key),
      value: Number(distribution.series?.[index]) || 0,
    })),
    [config.distribution, distribution],
  );
  const hasSide = hasAmounts || Boolean(distribution);

  // 月圖月份來自 trend_months，與頁面日期區間無關 → 鑽取帶該月整月
  const handleBarClick = useCallback(
    ({ status, month }) => {
      const range = monthRange(month);
      if (!range) {
        return;
      }
      navigate(buildDrillDownHref(config.key, config.listRoute, { status, range }));
    },
    [config.key, config.listRoute, navigate],
  );

  return (
    <div className={isFetching ? 'opacity-70 transition-opacity' : 'transition-opacity'}>
      <div className={cn('grid grid-cols-1 gap-3', hasSide && '@5xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]')}>
        <section className={cn(CHART_SECTION_CLASS, hasAmounts && distribution && '@5xl:row-span-2')}>
          <header className="flex flex-wrap items-baseline justify-between gap-x-3">
            <h3 className="font-semibold text-foreground">
              {config.title}
              {config.phases ? '各月進度' : '月別狀態'}
            </h3>
            <div className="flex items-center gap-1" role="group" aria-label="月圖月數">
              {DASHBOARD_TREND_MONTH_OPTIONS.map((months) => (
                <Button
                  key={months}
                  type="button"
                  size="sm"
                  variant={months === trendMonths ? 'default' : 'outline'}
                  onClick={() => onTrendMonthsChange(months)}
                >
                  近{months}個月
                </Button>
              ))}
            </div>
            <span className="w-full text-xs text-muted-foreground">
              {config.phases
                ? '每列為該月建立的單據目前走到哪；點色段可查看單據'
                : '依目前狀態統計；點長條可查看該月單據'}
            </span>
          </header>
          {config.phases ? (
            <StatusProgressBars
              statusByMonth={moduleData?.statusByMonth}
              statusLabels={config.statusLabels}
              phases={config.phases}
              onBarClick={handleBarClick}
            />
          ) : (
            <StatusByMonthChart
              statusByMonth={moduleData?.statusByMonth}
              statusLabels={config.statusLabels}
              statusOrder={config.statusOrder}
              statusTones={config.statusTones}
              onBarClick={handleBarClick}
            />
          )}
        </section>
        {hasAmounts && (
          <section className={CHART_SECTION_CLASS}>
            <header className="flex flex-col gap-2">
              <h3 className="font-semibold text-foreground">{config.title}金額</h3>
              <SelectCore
                id={`dashboard-${config.key}-currency`}
                label="幣別"
                options={currencyQuery.data ?? []}
                value={currencyId || moduleData.amounts.currencyId}
                onChange={(next) => onCurrencyChange(next ?? '')}
                isDisabled={currencyQuery.isPending}
              />
            </header>
            <AmountBreakdown amounts={moduleData.amounts} />
          </section>
        )}
        {distribution && (
          <section className={cn(CHART_SECTION_CLASS, hasAmounts && '@5xl:col-start-2')}>
            <header className="flex flex-wrap items-baseline justify-between gap-x-3">
              <h3 className="font-semibold text-foreground">{config.distribution.title}</h3>
              <span className="text-xs text-muted-foreground">
                單據期間 {period ? `${period.from}～${period.to}` : ''}
              </span>
            </header>
            <BreakdownSummary
              items={distributionItems}
              totalLabel="單據數"
              formatValue={(value) => `${formatCount(value)} 筆`}
              emptyText="此區間沒有單據。"
            />
          </section>
        )}
      </div>
    </div>
  );
};

ModuleDashboardPanel.propTypes = {
  config: PropTypes.object.isRequired,
  moduleData: PropTypes.object,
  currencyId: PropTypes.string,
  onCurrencyChange: PropTypes.func.isRequired,
  trendMonths: PropTypes.number.isRequired,
  onTrendMonthsChange: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
};

export default ModuleDashboardPanel;
